// "The bid, as the customer will see it" — the right-hand panel of Ben's approved
// step 3 (docs/build-room/demos/item-841.html). Step 3 shows it live as the answers
// change; Pricing and Review show it for what is saved, so every place a monthly price
// appears shows the nightly line, the added lines and the total.

import { fetchFloorCareLines, fetchFloorServices, fetchPriceLines, type BidRow, type PriceLines } from './api';
import { answersFromBid, customerView, type CustomerView, type Line } from './services-logic';
import { money } from './format';
import { useBidLoad } from './use-bid-load';

export function CustomerPricePanel({ price, view }: { price: PriceLines | null; view: CustomerView }) {
  const row = 'flex justify-between gap-2 border-b border-tcs-border py-1.5 text-sm';
  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg p-4 lg:sticky lg:top-3">
      <h3 className="mb-2 text-[15px] font-semibold text-tcs-text">The bid, as the customer will see it</h3>
      <div className={row}><span className="font-semibold">Nightly cleaning</span><span className="whitespace-nowrap font-semibold tabular-nums">{money(price?.nightly_monthly)}</span></div>
      {view.consLabel && (
        <div className={row}><span>{view.consLabel}</span><span className="whitespace-nowrap font-semibold tabular-nums">{money(price?.consumables_monthly)}</span></div>
      )}
      {view.floorIncluded && (
        <div className={row}>
          <span>Periodic floor care, included<br /><small className="text-xs text-tcs-text-muted">{view.floorDetail}</small></span>
          <span className="whitespace-nowrap font-semibold tabular-nums">{money(price?.floor_care_monthly)}</span>
        </div>
      )}
      <div className="flex justify-between pb-1 pt-2.5 text-lg font-bold"><span>Monthly price</span><span className="tabular-nums">{money(price?.total_monthly)}</span></div>
      <div className="mt-2.5 text-[13px] text-tcs-text-muted">
        <div className="py-0.5 font-semibold text-tcs-text">In the contract, not in the monthly price</div>
        {view.side.map((s) => <div key={s} className="py-0.5">• {s}</div>)}
      </div>
      {view.errors.map((e) => <div key={e} role="alert" className="mt-2 text-[12.5px] text-st-danger-fg">{e}</div>)}
    </div>
  );
}

/** The saved bid's price lines and the panel's words — for Pricing and Review. */
export function useSavedPrice(bid: BidRow) {
  return useBidLoad<{ price: PriceLines | null; view: CustomerView } | null>(
    async () => {
      const [prices, lines, services] = await Promise.all([fetchPriceLines([bid.bid_id]), fetchFloorCareLines(bid.bid_id), fetchFloorServices()]);
      const names = new Map(services.map((s) => [s.code, s.name]));
      const asLines: Line[] = lines.map((l) => ({
        line_id: l.line_id, floor_type: l.floor_type ?? '', service_code: l.service_code, sqft: l.sqft != null ? Number(l.sqft) : null,
        rate: Number(l.rate_per_sf), times_per_year: l.times_per_year ?? 0, edited: l.rate_edited,
      }));
      const a = answersFromBid(bid, asLines, { pctDefault: 0, pctMin: 0, pctMax: 100, markupDefault: 0, markupMin: 0, markupMax: 100 }, false);
      const view = customerView(a, (c) => names.get(c) ?? c, {});
      // a saved bid with floor care baked in and no line yet: step 3 has not been saved
      if (a.floor && a.floorMode === 'baked_in' && a.lines.length === 0) view.errors.push('Save step 3, Services, to add the floor-care lines.');
      return { price: prices[0] ?? null, view };
    },
    [bid.bid_id, bid.updated_at],
    null,
  );
}
